import {Trans} from '@lingui/macro';
import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {addQueryToPath} from '../../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {
  Handler,
  HandlerOf,
  PredicateOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {PaletteColorsEnum} from '../../../../../../../libs/shared/ui/src/styles/paletteModel';
import {useConstant} from '../../../../../../../srcCommon/helpers/hookHelpers';
import {CancelToken} from '../../../../../../helpers/asyncCancelHelpers';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {
  buildTeammateSearchUrl,
  SearchTeammateSortsEnum,
} from '../../../../../../helpers/models/teammateHelpers';
import {ApiSearchTeammates} from '../../../../../../models/api/searchTeammateModel';
import {
  TeammateAccountStatusesEnum,
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../../../../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {FindOnlineStatus} from '../../../../../../store/data/teammates/onlineTeammatesSelectors';
import {DropdownCategory} from '../../../../../common/dropdowns/dropdownCategory';
import {DeprecatedSearchDropdown} from '../../../../../common/dropdowns/search/deprecatedSearchDropdown';
import {DropdownFetcher} from '../../../../../common/dropdowns/source/asyncDropdownSource';
import {RowRendererOf} from '../../../../../common/dropdowns/source/dropdownSource';
import {useAsyncDropdownSource} from '../../../../../common/dropdowns/source/hooks/useAsyncDropdownSource';
import {BulkActionConfirmationDialog} from '../../actions/bulk/bulkActionConfirmationDialog';
import {AssignDropdownAddTeammateCardContainer} from './assignDropdownAddTeammateCardContainer';
import {AssignDropdownItem} from './assignDropdownItem';
import {AssignDropdownUnassignItem} from './assignDropdownUnassignItem';

/*
 * Constants.
 */

const assignDropdownWidth = 320;
const assignDropdownMaxContentHeight = 296;

/*
 * Types.
 */

export interface AssignOptions {
  teammate: TeammateModel | undefined;
  openedAt: number | undefined;
  interfaceEventSection: string;
}

export enum AssignDropdownItemTypesEnum {
  RECENT_ASSIGNEE,
  RECENT_ASSIGNEE_HEADER,
  UNASSIGNED_ASSIGNEE,
  TEAMMATE_ASSIGNEE,
  TEAMMATE_ASSIGNEE_HEADER,
}

interface DropdownRecentAssigneeItem {
  type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE;
  teammate: TeammateModel;
}

interface DropdownRecentAssigneeHeaderItem {
  type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE_HEADER;
}

interface DropdownUnassignedItem {
  type: AssignDropdownItemTypesEnum.UNASSIGNED_ASSIGNEE;
}

interface DropdownTeammateAssigneeItem {
  type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE;
  teammate: TeammateModel;
}

interface DropdownTeammateAssigneeHeaderItem {
  type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE_HEADER;
}

export type AssigneeDropdownItem =
  | DropdownRecentAssigneeItem
  | DropdownRecentAssigneeHeaderItem
  | DropdownUnassignedItem
  | DropdownTeammateAssigneeItem
  | DropdownTeammateAssigneeHeaderItem;

/*
 * Props.
 */

interface AssignDropdownProps extends DropdownProps {
  user: TeammateModel;
  isTeamMember: PredicateOf<TeammateModel>;
  findOnlineStatus: FindOnlineStatus;
  /** The assignee for all the selected conversations. */
  assigneeAll?: TeammateModel;
  onAssign: HandlerOf<AssignOptions>;
  onBulkAssign: HandlerOf<AssignOptions>;
  /**
   * Links to the page in settings where the user can invite a teammate, or
   * opens the invite teammate popover.
   */
  buildNavigateToAddTeammate: (hasOtherAssignableTeammates: boolean) => Handler | undefined;
  isBulkActionRequired: boolean;
  conversationInboxIds: ReadonlyArray<number> | null;
  companyUrl: string;
}

/*
 * Style.
 */

const StyledFooterWrapperDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Component.
 */

export const LazyAssignDropdown: FC<AssignDropdownProps> = (props) => {
  const [teammateToBulkAssign, setTeammateToBulkAssign] = useState<TeammateModel | null>();
  const openedAt = useConstant(() => performance.now());
  const isSearchingRef = useRef(false);
  const [hasOtherAssignableTeammates, setHasOtherAssignableTeammates] = useState(true);
  const [bulkAssignInterfaceSection, setBulkAssignInterfaceSection] = useState<string | undefined>(undefined);

  /*
   * Event handlers.
   */

  const onRequestClose = () => {
    setTeammateToBulkAssign((prevTeammateToBulkAssign) => {
      // If we have a confirmation open, don't close.
      if (prevTeammateToBulkAssign !== undefined) {
        return prevTeammateToBulkAssign;
      }

      // Otherwise, request close.
      if (props.onRequestClose) {
        props.onRequestClose();
      }

      return null;
    });
  };

  const onAssignRequest: HandlerOf<{
    teammate: TeammateModel | undefined;
    interfaceEventSection: string;
  }> = ({teammate, interfaceEventSection}) => {
    const {onAssign, isBulkActionRequired} = props;

    // If we need to do a bulk action, ask for confirmation.
    if (isBulkActionRequired) {
      setTeammateToBulkAssign(teammate || null);
      setBulkAssignInterfaceSection(interfaceEventSection);
      return;
    }

    // Otherwise, snooze.
    onAssign({teammate, openedAt, interfaceEventSection});
  };

  const onHideBulkAssignConfirmation = () => {
    setTeammateToBulkAssign(undefined);
    setBulkAssignInterfaceSection(undefined);
  };

  const onConfirmBulkAssign = () => {
    const teammate = teammateToBulkAssign || undefined;
    const section = bulkAssignInterfaceSection ?? '';

    onHideBulkAssignConfirmation();

    props.onBulkAssign({
      teammate,
      openedAt,
      interfaceEventSection: section,
    });

    if (props.onRequestClose) {
      props.onRequestClose();
    }
  };

  /*
   * Render Helpers.
   */

  const maybeRenderBulkAssignConfirmation = () => {
    if (teammateToBulkAssign === undefined) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        actionType={teammateToBulkAssign === null ? BulkActionTypesEnum.UNASSIGN : BulkActionTypesEnum.ASSIGN}
        onConfirmClick={onConfirmBulkAssign}
        onRequestClose={onHideBulkAssignConfirmation}
      />
    );
  };

  const renderRow: RowRendererOf<AssigneeDropdownItem> = (item, index) => {
    const {user, assigneeAll, isTeamMember, findOnlineStatus} = props;

    // Render static rows.
    if (
      item.type !== AssignDropdownItemTypesEnum.RECENT_ASSIGNEE &&
      item.type !== AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE
    ) {
      return renderStaticRow(item, index, assigneeAll);
    }

    const {teammate, type} = item;
    const teammateSection = type === AssignDropdownItemTypesEnum.RECENT_ASSIGNEE ? 'recent' : 'all_items';
    const interfaceEventSection = isSearchingRef.current ? 'search_results' : teammateSection;
    return (
      <AssignDropdownItem
        key={teammate.id}
        index={index}
        teammate={teammate}
        isUser={teammate === user}
        isAssignee={teammate === assigneeAll}
        isTeamMember={isTeamMember(teammate)}
        onlineStatus={findOnlineStatus(teammate)}
        onSelect={() => onAssignRequest({teammate, interfaceEventSection})}
      />
    );
  };

  const renderStaticRow = (
    item: DropdownUnassignedItem | DropdownTeammateAssigneeHeaderItem | DropdownRecentAssigneeHeaderItem,
    index: number,
    assigneeAll?: TeammateModel,
  ) => {
    switch (item.type) {
      case AssignDropdownItemTypesEnum.RECENT_ASSIGNEE_HEADER:
        return (
          <DropdownCategory>
            <Trans id="dropdown-assignees-section-recently-used">Recently used</Trans>
          </DropdownCategory>
        );
      case AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE_HEADER:
        return (
          <DropdownCategory>
            <Trans id="dropdown-assignees-section-all-teammates">All teammates</Trans>
          </DropdownCategory>
        );
      default:
        return (
          <AssignDropdownUnassignItem
            index={index}
            assignee={assigneeAll}
            iconName="none"
            color={PaletteColorsEnum.GREY}
            shouldHideBackground
            onSelect={() => onAssignRequest({teammate: undefined, interfaceEventSection: 'all_items'})}
          />
        );
    }
  };

  /*
   * Dropdown source.
   */

  const {assigneeAll, companyUrl, user, conversationInboxIds} = props;

  const fetcher: DropdownFetcher<AssigneeDropdownItem, FetchAssigneesPaginationData> = async (
    query,
    items,
    cancelToken,
    paginationData,
  ) => {
    // Update the searching state.
    isSearchingRef.current = Boolean(query);

    // Build the URL and options.
    const offset = paginationData?.offset || 0;
    const url = buildTeammateSearchUrl(companyUrl, query, {
      offset,
      accountStatuses: [TeammateAccountStatusesEnum.ACTIVE],
      licenseTypes: [TeammateLicenseTypesEnum.FULL, TeammateLicenseTypesEnum.LIGHT],
      sort: SearchTeammateSortsEnum.NAME,
    });

    // Fetch the items.
    const fetchTeammatesPromise = fetchApiAsync(ApiSearchTeammates, url, {cancelToken});
    const initialItemsPromise = maybeFetchInitialItemsAsync({
      cancelToken,
      conversationInboxIds,
      paginationData,
      query,
      userUrl: user.url,
      assigneeAll,
    });
    const [initialItems, fetchedTeammates] = await Promise.all([initialItemsPromise, fetchTeammatesPromise]);

    const teammateItems = fetchedTeammates.teammates.map((teammate) => ({
      type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE,
      teammate,
    }));

    // Maybe show invite footer.
    if (
      !paginationData?.hasFetchedInitialItems &&
      fetchedTeammates.teammates.every((t) => t.id === user.id)
    ) {
      setHasOtherAssignableTeammates(false);
    }

    const allItems = items.concat(...items, ...initialItems, ...teammateItems);

    return {
      items: allItems,
      canLoadMore: fetchedTeammates.teammates.length === fetchedTeammates.limit,
      paginationData: {
        hasFetchedInitialItems: true,
        offset: offset + fetchedTeammates.teammates.length,
      },
    };
  };

  const dropdownSource = useAsyncDropdownSource(
    {
      fetcher,
      renderRow,
    },
    [companyUrl, assigneeAll, conversationInboxIds],
  );

  /*
   * Render.
   */

  return (
    <>
      <DeprecatedSearchDropdown
        {...props}
        footer={maybeRenderAddTeammateCard(props, hasOtherAssignableTeammates)}
        title={<Trans id="dropdown-assign-title">Assign</Trans>}
        width={assignDropdownWidth}
        source={dropdownSource}
        onRequestClose={onRequestClose}
        maxContentHeight={assignDropdownMaxContentHeight}
      />
      {maybeRenderBulkAssignConfirmation()}
    </>
  );
};

/*
 * Render helpers.
 */

function maybeRenderAddTeammateCard(props: AssignDropdownProps, hasOtherAssignableTeammates: boolean) {
  const navigateToAddTeammate = props.buildNavigateToAddTeammate(hasOtherAssignableTeammates);

  if (!navigateToAddTeammate) {
    return null;
  }

  return (
    <StyledFooterWrapperDiv>
      <AssignDropdownAddTeammateCardContainer onClick={navigateToAddTeammate} />
    </StyledFooterWrapperDiv>
  );
}

/*
 * Fetch helpers.
 */

interface FetchAssigneesPaginationData {
  hasFetchedInitialItems: boolean;
  offset: number;
}

type InitialItemsFetchOptions = {
  cancelToken: CancelToken;
  conversationInboxIds: ReadonlyArray<number> | null;
  paginationData?: FetchAssigneesPaginationData;
  query: string;
  userUrl: string;
  assigneeAll?: TeammateModel;
};

async function maybeFetchInitialItemsAsync(options: InitialItemsFetchOptions) {
  if (options.paginationData?.hasFetchedInitialItems) {
    // We've already fetched the initial items.
    return [];
  }

  if (options.query) {
    // We don't need the initial items while searching.
    return [];
  }

  const recentAssignees = await maybeFetchRecentAssigneesAsync(options);

  const dropdownItems: AssigneeDropdownItem[] = [];

  // Current assignee item.
  if (options.assigneeAll) {
    dropdownItems.push({
      type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE,
      teammate: options.assigneeAll,
    });
  }

  // Unassign item.
  dropdownItems.push({
    type: AssignDropdownItemTypesEnum.UNASSIGNED_ASSIGNEE,
  });

  // Recent assignees.
  if (recentAssignees.length > 0) {
    dropdownItems.push(
      {
        type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE_HEADER,
      },
      ...recentAssignees.map((teammate) => ({
        type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE,
        teammate,
      })),
    );
  }

  // All teammates header.
  dropdownItems.push({
    type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE_HEADER,
  });

  return dropdownItems;
}

async function maybeFetchRecentAssigneesAsync(options: InitialItemsFetchOptions) {
  if (!options.conversationInboxIds || options.conversationInboxIds.length === 0) {
    // We don't inbox ids to fetch the recent assignees for.
    return [];
  }

  const recentAssigneesUrl = addQueryToPath(`${options.userUrl}/recent_assignees`, {
    inbox_ids: options.conversationInboxIds.join(','),
  });
  const searchTeammates = await fetchApiAsync(ApiSearchTeammates, recentAssigneesUrl, {
    cancelToken: options.cancelToken,
  });
  return searchTeammates.teammates;
}
